import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Gift, CheckCircle, Clock, XCircle, Info, AlertTriangle } from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EarnProMonth() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [earnProEnabled, setEarnProEnabled] = useState(false);
  const [unlockDate, setUnlockDate] = useState(null);

  // Platform URLs
  const [platformUrls, setPlatformUrls] = useState({
    youtube: '',
    tiktok: '',
    instagram: '',
    facebook: ''
  });

  useEffect(() => {
    const loadConfigAndStatus = async () => {
      try {
        const res = await fetch(`${API}/app-config`);
        if (res.ok) {
          const data = await res.json();
          setEarnProEnabled(Boolean(data.earn_pro_enabled));
          setUnlockDate(data.unlock_date || null);

          if (data.earn_pro_enabled) {
            await checkSubmissionStatus();
          }
        }
      } catch (error) {
        console.error('Failed to load app config', error);
        setEarnProEnabled(false);
      } finally {
        setLoading(false);
      }
    };

    loadConfigAndStatus();
  }, [user]);

  const checkSubmissionStatus = async () => {
    // Check backend for submission only when enabled
    try {
      const res = await fetch(`${API}/earn-pro/status`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.has_submission) {
          setHasSubmitted(true);
          setSubmissionStatus(data.latest_submission?.status || data.status || 'pending');
          return;
        }
      }
    } catch (error) {
      console.error('Error checking submission:', error);
    }

    // Also check localStorage as backup (front-end lock)
    const localKey = `earn_pro_submitted_${user?.id || user?.email}`;
    const localSubmitted = localStorage.getItem(localKey);
    if (localSubmitted) {
      setHasSubmitted(true);
      setSubmissionStatus(JSON.parse(localSubmitted).status || 'pending');
    }
  };

  const getFilledCount = () => {
    return Object.values(platformUrls).filter(url => url.trim() !== '').length;
  };

  const getTotalPlatforms = () => {
    return 4; // YouTube, TikTok, Instagram, Facebook
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!earnProEnabled) {
      toast.error('Earn Free Pro Month unlocks 30 days after launch');
      return;
    }

    // Validate at least one URL
    const filledUrls = Object.entries(platformUrls).filter(([_, url]) => url.trim() !== '');
    if (filledUrls.length === 0) {
      toast.error('Please provide at least one platform URL');
      return;
    }

    // Validate URL formats
    for (const [platform, url] of filledUrls) {
      if (!url.startsWith('http')) {
        toast.error(`Invalid URL for ${platform}. Must start with http:// or https://`);
        return;
      }
    }

    // Build payload with only filled platform URLs
    const platform_posts = Object.fromEntries(filledUrls);

    setSubmitting(true);
    try {
      // Try backend submission
      const res = await fetch(`${API}/earn-pro/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          platform_posts
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Submission received!');
        setHasSubmitted(true);
        setSubmissionStatus('pending');

        // Set localStorage lock
        const localKey = `earn_pro_submitted_${user?.id || user?.email}`;
        localStorage.setItem(localKey, JSON.stringify({
          submitted: true,
          status: 'pending',
          timestamp: new Date().toISOString()
        }));
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Submission failed');
      }
    } catch (error) {
      console.error('Backend submission failed:', error);
      // Fallback to front-end only submission
      toast.error('Submission disabled until day 30');
    } finally {
      setSubmitting(false);
    }
  };

  if (!earnProEnabled && !loading) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-[#8A93A6] mb-2">
            <Link to="/app/account" className="hover:text-white">ACCOUNT</Link>
            <span>/</span>
            <span className="text-white">Rewards</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Rewards</h1>
        </div>

        <Card className="glass border-dashed border-white/10">
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 text-yellow-500" />
              <div>
                <h3 className="text-xl font-semibold text-white">Available after launch day 30</h3>
                <p className="text-[#8A93A6] text-sm">Earn Free Pro Month submissions open once gates unlock.</p>
              </div>
            </div>
            {unlockDate && (
              <p className="text-sm text-[#8A93A6]">Expected unlock date: {unlockDate}</p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  // Already submitted state
  if (hasSubmitted) {
    const StatusIcon = submissionStatus === 'approved' ? CheckCircle :
                       submissionStatus === 'rejected' ? XCircle : Clock;
    const statusColor = submissionStatus === 'approved' ? 'text-green-500' :
                        submissionStatus === 'rejected' ? 'text-red-500' : 'text-yellow-500';

    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="mb-6">
          <div className="flex itemscenter gap-2 text-sm text-[#8A93A6] mb-2">
            <Link to="/app/account" className="hover:text-white">ACCOUNT</Link>
            <span>/</span>
            <span className="text-white">Rewards</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Rewards</h1>
        </div>

        <Card className="glass">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <StatusIcon className={`h-16 w-16 ${statusColor} mx-auto mb-4`} />
              <h3 className="text-xl font-bold text-white mb-2">Already Submitted</h3>
              <p className="text-[#8A93A6] mb-4">
                You have already submitted your content for review. This is a one-time offer per account.
              </p>
              <Badge variant="outline" className={statusColor}>
                Status: {submissionStatus}
              </Badge>
              {submissionStatus === 'pending' && (
                <p className="text-sm text-[#8A93A6] mt-4">
                  Your submission is pending admin approval. Check back soon!
                </p>
              )}
              {submissionStatus === 'approved' && (
                <p className="text-sm text-green-500 mt-4">
                  Your free Pro month has been applied to your account!
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-[#8A93A6] mb-2">
          <Link to="/app/account" className="hover:text-white">ACCOUNT</Link>
          <span>/</span>
          <span className="text-white">Rewards</span>
        </div>
        <div className="flex items-center gap-3 mb-2">
          <Gift className="h-8 w-8 text-blue-500" />
          <h1 className="text-3xl font-bold text-white">Rewards</h1>
        </div>
        <p className="text-[#8A93A6]">
          Post about Consistency on your platforms and get 1 month of Pro for free!
        </p>
      </div>

      {/* Important Notes */}
      <Alert className="bg-yellow-500/10 border-yellow-500/30 mb-6">
        <AlertTriangle className="h-4 w-4 text-yellow-500" />
        <AlertDescription className="text-yellow-400 text-sm">
          <strong>One-time offer:</strong> You can only submit once per account. Manual admin approval required.
        </AlertDescription>
      </Alert>

      <Alert className="bg-blue-500/10 border-blue-500/30 mb-6">
        <Info className="h-4 w-4 text-blue-500" />
        <AlertDescription className="text-blue-400 text-sm">
          <strong>How to get your video link:</strong> Use the Share button on your video and paste the link here.
        </AlertDescription>
      </Alert>

      {/* Submission Form */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Submit Your Content
            <Badge variant="outline">
              {getFilledCount()} / {getTotalPlatforms()} platforms
            </Badge>
          </CardTitle>
          <CardDescription>
            Share links to posts where you mentioned Consistency
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Platform URL Fields */}
            <div className="space-y-4">
              {/* YouTube */}
              <div>
                <Label>YouTube (Optional)</Label>
                <Input
                  type="url"
                  value={platformUrls.youtube}
                  onChange={(e) => setPlatformUrls({...platformUrls, youtube: e.target.value})}
                  placeholder="https://youtube.com/watch?v=..."
                  className="glass"
                />
              </div>

              {/* TikTok */}
              <div>
                <Label>TikTok (Optional)</Label>
                <Input
                  type="url"
                  value={platformUrls.tiktok}
                  onChange={(e) => setPlatformUrls({...platformUrls, tiktok: e.target.value})}
                  placeholder="https://tiktok.com/@user/video/..."
                  className="glass"
                />
              </div>

              {/* Instagram */}
              <div>
                <Label>Instagram (Optional)</Label>
                <Input
                  type="url"
                  value={platformUrls.instagram}
                  onChange={(e) => setPlatformUrls({...platformUrls, instagram: e.target.value})}
                  placeholder="https://instagram.com/p/..."
                  className="glass"
                />
              </div>

              {/* Facebook */}
              <div>
                <Label>Facebook (Optional)</Label>
                <Input
                  type="url"
                  value={platformUrls.facebook}
                  onChange={(e) => setPlatformUrls({...platformUrls, facebook: e.target.value})}
                  placeholder="https://facebook.com/..."
                  className="glass"
                />
              </div>
            </div>

            <Alert className="bg-white/5 border-white/10">
              <Info className="h-4 w-4 text-[#8A93A6]" />
              <AlertDescription className="text-[#8A93A6] text-sm">
                You don't need to fill all platforms. Submit at least one to qualify.
              </AlertDescription>
            </Alert>

            <Button
              type="submit"
              disabled={submitting || getFilledCount() === 0}
              className="w-full"
            >
              {submitting ? 'Submitting...' : 'Submit for Review'}
            </Button>

            <p className="text-xs text-center text-[#8A93A6]">
              Your submission will be reviewed by our team. You'll be notified once approved.
            </p>
          </form>
        </CardContent>
      </Card>

      {/* Requirements */}
      <Card className="glass mt-6">
        <CardHeader>
          <CardTitle className="text-base">Requirements</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-[#8A93A6]">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>Post must mention Consistency or show the app</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>Content must be publicly accessible</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>One submission per account (lifetime)</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span>Manual admin approval required</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
